import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Layout, PageHeader } from "@/components/Layout";
import { fmtRM, fmtRMSigned } from "@/lib/format";
import { useLang } from "@/context/LanguageContext";
import { Wallet, Layers, Landmark, TrendingUp, ArrowUpDown } from "lucide-react";

function SummaryCard({ label, value, icon: Icon, accent, valueClass, testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-5" data-testid={testid}>
      <div className="flex items-center gap-2 text-slate-500 text-[11px] uppercase tracking-wide">
        <span className={`h-7 w-7 rounded-md flex items-center justify-center ${accent}`}><Icon className="h-4 w-4" /></span>
        {label}
      </div>
      <div className={`font-mono-fig text-2xl font-bold mt-2 ${valueClass || "text-slate-900"}`}>{value}</div>
    </div>
  );
}

export default function AllBranchesOverview() {
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [month, setMonth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState({ key: "monthProfit", dir: "desc" });

  useEffect(() => {
    setLoading(true);
    api
      .get("/director/branches-overview", { params: month ? { month } : {} })
      .then((r) => {
        setData(r.data);
        if (!month) setMonth(r.data.month);
      })
      .finally(() => setLoading(false));
  }, [month]);

  const toggleSort = (key) =>
    setSort((s) => ({ key, dir: s.key === key && s.dir === "desc" ? "asc" : "desc" }));

  const rows = [...(data?.branches || [])].sort((a, b) => {
    const k = sort.key;
    const va = k === "branch" ? a.branch : a[k];
    const vb = k === "branch" ? b.branch : b[k];
    if (k === "branch") return sort.dir === "asc" ? va.localeCompare(vb) : vb.localeCompare(va);
    return sort.dir === "asc" ? va - vb : vb - va;
  });

  const totals = data?.totals;

  const Th = ({ k, label, right }) => (
    <th
      className={`px-4 py-3 font-medium cursor-pointer select-none hover:text-slate-900 ${right ? "text-right" : "text-left"}`}
      onClick={() => toggleSort(k)}
      data-testid={`ov-sort-${k}`}
    >
      <span className={`inline-flex items-center gap-1 ${right ? "flex-row-reverse" : ""}`}>
        {label} <ArrowUpDown className={`h-3 w-3 ${sort.key === k ? "text-slate-900" : "text-slate-300"}`} />
      </span>
    </th>
  );

  return (
    <Layout>
      <PageHeader
        title={t("ov.title")}
        subtitle={t("ov.subtitle")}
        action={
          <div className="flex items-center gap-2">
            <label className="text-xs text-slate-500">{t("ov.month_label")}</label>
            <select
              data-testid="ov-month-select"
              value={month || ""}
              onChange={(e) => setMonth(e.target.value)}
              className="border border-slate-300 rounded-md px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-slate-900/10"
            >
              {(data?.months || []).map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </div>
        }
      />

      {totals && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <SummaryCard testid="ov-total-cash" label={t("ov.total_cash")} value={fmtRM(totals.cashBalance)} icon={Wallet} accent="bg-blue-50 text-blue-600" />
          <SummaryCard testid="ov-total-active" label={t("ov.total_active")} value={totals.activeLoans} icon={Layers} accent="bg-violet-50 text-violet-600" />
          <SummaryCard testid="ov-total-outstanding" label={t("ov.total_outstanding")} value={fmtRM(totals.outstanding)} icon={Landmark} accent="bg-amber-50 text-amber-600" valueClass="text-amber-700" />
          <SummaryCard testid="ov-total-profit" label={t("ov.month_profit")} value={fmtRMSigned(totals.monthProfit)} icon={TrendingUp} accent="bg-emerald-50 text-emerald-600" valueClass={totals.monthProfit >= 0 ? "text-emerald-700" : "text-red-600"} />
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-x-auto">
        <table className="w-full text-sm" data-testid="ov-table">
          <thead>
            <tr className="text-xs uppercase tracking-wider text-slate-500 bg-slate-50 border-b border-slate-200">
              <Th k="branch" label={t("ov.col_branch")} />
              <Th k="cashBalance" label={t("ov.col_cash")} right />
              <Th k="activeLoans" label={t("ov.col_active")} right />
              <Th k="outstanding" label={t("ov.col_outstanding")} right />
              <Th k="monthProfit" label={t("ov.col_month_profit")} right />
            </tr>
          </thead>
          <tbody>
            {rows.map((b) => (
              <tr key={b.branch} data-testid={`ov-row-${b.branch}`} className="border-b border-slate-50 hover:bg-slate-50">
                <td className="px-4 py-3 font-medium text-slate-900">
                  <Link to="/" onClick={() => localStorage.setItem("hoe_branch", b.branch)} className="hover:underline" data-testid={`ov-branch-link-${b.branch}`}>
                    {b.branchName || b.branch}
                  </Link>
                  <span className="text-xs text-slate-400 ml-1 font-mono-fig">{b.branch}</span>
                </td>
                <td className={`px-4 py-3 text-right font-mono-fig ${b.cashBalance < 0 ? "text-red-600" : "text-slate-800"}`}>{fmtRM(b.cashBalance)}</td>
                <td className="px-4 py-3 text-right font-mono-fig text-slate-700">{b.activeLoans}</td>
                <td className="px-4 py-3 text-right font-mono-fig text-amber-700">{fmtRM(b.outstanding)}</td>
                <td className={`px-4 py-3 text-right font-mono-fig font-semibold ${b.monthProfit >= 0 ? "text-emerald-700" : "text-red-600"}`}>{fmtRMSigned(b.monthProfit)}</td>
              </tr>
            ))}
          </tbody>
          {totals && rows.length > 0 && (
            <tfoot>
              <tr className="bg-slate-900 text-white font-semibold" data-testid="ov-totals-row">
                <td className="px-4 py-3">{t("ov.all_branches")}</td>
                <td className="px-4 py-3 text-right font-mono-fig">{fmtRM(totals.cashBalance)}</td>
                <td className="px-4 py-3 text-right font-mono-fig">{totals.activeLoans}</td>
                <td className="px-4 py-3 text-right font-mono-fig">{fmtRM(totals.outstanding)}</td>
                <td className="px-4 py-3 text-right font-mono-fig">{fmtRMSigned(totals.monthProfit)}</td>
              </tr>
            </tfoot>
          )}
        </table>
        {!loading && rows.length === 0 && (
          <div className="px-5 py-12 text-center text-slate-400">{t("ov.none")}</div>
        )}
      </div>

      <p className="text-xs text-slate-400 mt-3 max-w-3xl">{t("ov.note")}</p>
    </Layout>
  );
}
